import { Link } from '@tanstack/react-router';
import { scaleTime } from 'd3-scale';
import { timeMonth } from 'd3-time';
import { hubDate, isOverdue, lowerKey, type ActionItem, type GanttBundle, type GanttLastComment, type GanttNode } from '@pathonline/shared';
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent, type ReactNode, type Ref, type RefObject } from 'react';
import { useReadOnly } from '../api.ts';
import { FolderGlyph, FolderModal } from '../folders/FolderModal.tsx';
import { folderLook, folderText } from '../folders/model.ts';
import { hubDateTime, plural } from '../format.ts';
import { usePhoto } from '../photo.tsx';
import { ActionCard, ActionPopover, BUBBLE_STEP, DoneCard, extrasAt, isStalled, placeBubbles, PlusCard, type ActionPop } from './ActionPopover.tsx';
import {
  actionMarks,
  barPeople,
  canEditNode,
  canParent,
  day,
  daysLeft,
  depLinks,
  depPath,
  fmtLeft,
  initials,
  isGrant,
  peopleByName,
  ROW_H,
  timeDomain,
  typeLabel,
  type DepLink,
  type Filters,
  type Row,
} from './model.ts';

/** "2026-03", as the old axis. */
const ym = (m: Date) => `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
/** People drawn in the name column; the rest show as "+N" (old Gantt). */
const MAX_PEOPLE = 4;
/** The narrowest bar, in % of the track. */
const MIN_BAR = 0.6;

/** A person on the task: the photo, or the initials. */
export function Ring({ person, role, image, personId }: { person: string; role: string; image: string; personId?: string }) {
  return (
    <span className={`ring r-${role}`} title={`${person} — ${role}`}>
      {usePhoto(image, person, initials(person), personId)}
    </span>
  );
}

interface Props {
  bundle: GanttBundle;
  rows: Row[];
  filters: Filters;
  selected?: string;
  /** Opens a task's details panel; no id closes it (Escape). */
  onSelect: (nodeId?: string) => void;
  /** The focused grant ("Focus & expand"), if any. */
  focus?: string;
  onFocus: (grantId?: string) => void;
  /** Drag a task onto another: the second becomes the first's parent. */
  onReparent: (childId: string, parentId: string) => void;
  onThread: (nodeId: string) => void;
  /** Shown in the empty top-left cell: the task count and the save message. */
  corner?: ReactNode;
}

/** Rows on the left, bars on a month axis on the right. */
export function GanttChart({ bundle: b, rows, filters, selected, onSelect, focus, onFocus, onReparent, onThread, corner }: Props) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // A next action's popover, or the quick add behind a "+".
  const [pop, setPop] = useState<ActionPop | null>(null);
  const closePop = useCallback(() => setPop(null), []);
  // The task or grant whose folder is open.
  const [folder, setFolder] = useState<GanttNode | null>(null);
  const readOnly = useReadOnly();
  const card = useRef<CardApi>(null);
  // The track in px: the bubbles keep a bubble's width apart (old place()), which % cannot say.
  const [headTrack, tw] = useWidth<HTMLDivElement>();
  const today = new Date();
  // The axis follows the rows, the bundle's dates and the day: not worked out again on each resize, hover or drag.
  const { from, to, x, allMonths } = useMemo(() => {
    const [from, to] = timeDomain(rows, today, b);
    return { from, to, x: scaleTime().domain([from, to]).range([0, 100]), allMonths: timeMonth.range(from, to) };
  }, [rows, b.outputs, b.openActions, today.toDateString()]);
  // About a dozen labels whatever the range: every month, 2 or 3 months, half-year or year.
  const step = [1, 2, 3, 6, 12, 24].find((s) => allMonths.length / s <= 14) ?? 24;
  const months = allMonths.filter((m) => (m.getFullYear() * 12 + m.getMonth()) % step === 0);
  const todayPct = today >= from && today <= to ? x(today) : null;
  const todayStr = hubDate();
  const pct = (d: Date) => Math.min(100, Math.max(0, x(d)));
  const byId = new Map(b.nodes.map((n) => [n.id, n]));
  // Every month gets a grid line, as old; only every `step`-th gets a label. The same elements while the
  // range stays (allMonths and x follow from it): a resize draws every row again, and React skips these.
  const grid = useMemo(
    () => allMonths.map((m) => <span key={m.toISOString()} className="g-grid" style={{ left: `${x(m)}%` }} />),
    [from.getTime(), to.getTime()],
  );
  // A bar from either date: with one, both ends are that day.
  const spans = rows.map((r): [number, number] | null => {
    const start = r.node ? day(r.node.start) : null;
    const end = r.node ? day(r.node.end) : null;
    return start || end ? [pct((start ?? end)!), pct((end ?? start)!)] : null;
  });
  // Lines only between rows that drew a bar: a date that cannot be read draws neither.
  const links = depLinks(rows, (i) => spans[i] !== null);

  const outputsOf = (id: string) => b.outputs.filter((o) => o.nodeId === id);
  // Each task's lead (the first one), once per bundle.
  const leads = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of b.assignments) if (a.role === 'leads' && !m.has(a.nodeId)) m.set(a.nodeId, a.person);
    return m;
  }, [b.assignments]);
  // The people by name, once per bundle: the bars' rings and the lead's photo on the card.
  const byName = useMemo(() => peopleByName(b), [b.people]);

  // Escape closes the innermost thing first (old keydown, DP:7678-7680): the details panel, then Focus.
  // Not while typing, and not under a dialog: the comment thread closes itself. A popover that handles
  // Escape calls preventDefault().
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      card.current?.hide();
      const t = e.target instanceof Element ? e.target : null;
      const typing = !!t?.closest('textarea, select, [contenteditable="true"]') || (t instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit'].includes(t.type));
      if (typing || document.querySelector('[aria-modal="true"]')) return;
      if (selected) onSelect(undefined);
      else if (focus) onFocus(undefined);
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [selected, focus, onSelect, onFocus]);

  /** Pointer handlers that show `body()` in the hover card. A mouse only: a tap opens the panel. */
  const hover = (body: () => ReactNode) => ({
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType === 'mouse') card.current?.show(e, body());
    },
    onPointerMove: (e: PointerEvent) => card.current?.move(e),
    onPointerLeave: () => card.current?.hide(),
    onPointerDown: () => card.current?.hide(),
  });
  const nodeCard = (n: GanttNode) => {
    const lead = leads.get(n.id) ?? '';
    const p = byName.get(lowerKey(lead));
    return <NodeCard n={n} lead={lead} image={p?.image ?? ''} personId={p?.personId} canEdit={canEditNode(b, n)} />;
  };

  /** The popover opens by the bubble's centre: the same for a click, a tap and the keyboard. */
  const centre = (el: Element) => {
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };
  /** An open action: the lead's initials; filled on its deadline (red when overdue), hollow without one. */
  const bubble = (n: GanttNode, a: ActionItem, hollow: boolean, style?: CSSProperties) => (
    <button
      key={a.id}
      type="button"
      className={`g-act ${hollow ? 'hollow' : isOverdue(a.due) ? 'late' : ''}`}
      style={style}
      onClick={(e) => setPop({ kind: 'action', nodeId: n.id, action: a, ...centre(e.currentTarget) })}
      aria-label={`Next action: ${a.text} — ${a.lead}${a.due ? `, deadline ${a.due}` : ', no deadline'}`}
      {...hover(() => <ActionCard a={a} />)}
    >
      {initials(a.lead)}
    </button>
  );
  /** The dashed "+" to add one (old Actions.gs:619-631); amber on an ACTIVE task with no open action. */
  const plusBubble = (n: GanttNode, stalled: boolean) => (
    <button
      type="button"
      className={`g-act g-act-plus ${stalled ? 'stalled' : ''}`}
      onClick={(e) => setPop({ kind: 'add', nodeId: n.id, ...centre(e.currentTarget) })}
      aria-label={stalled ? 'No next action yet — add one' : 'Add a next action'}
      {...hover(() => <PlusCard stalled={stalled} />)}
    >
      +
    </button>
  );
  /** The green ✓ at the bar's end: the task's completed actions, with their count past one. None: no chip. */
  const doneBubble = (n: GanttNode) => {
    const d = b.doneActions?.[n.id];
    if (!d?.count) return null;
    return (
      <button
        type="button"
        className="g-act g-done"
        onClick={(e) => setPop({ kind: 'done', nodeId: n.id, ...centre(e.currentTarget) })}
        aria-label={`${plural(d.count, 'completed next action')} — show them`}
        {...hover(() => <DoneCard d={d} />)}
      >
        {d.count > 1 ? `✓${d.count}` : '✓'}
      </button>
    );
  };
  /** Its folder (folders/): its look, or null for no icon (the caller may not open it). */
  const lookOf = (n: GanttNode) => folderLook(b.folders?.[n.id], readOnly);
  /**
   * The folder, where the old Files link was: first after the bar end, before the actions (old Actions.gs:567-570).
   * Amber with files or notes; faint and dashed when empty, for someone who may add.
   */
  const folderBubble = (n: GanttNode) => {
    const look = lookOf(n);
    const f = b.folders?.[n.id];
    if (!look || !f) return null;
    return (
      <button
        type="button"
        className={`g-act g-files ${look === 'empty' ? 'g-files-add' : ''}`}
        onClick={() => setFolder(n)}
        aria-label={`Files of ${n.label}: ${folderText(f)}`}
        {...hover(() => <FolderCard what={folderText(f)} empty={look === 'empty'} />)}
      >
        <FolderGlyph />
      </button>
    );
  };

  return (
    <div className="gantt" role="table" aria-label="Tasks on a timeline">
      <div className="g-row g-head" role="row">
        <div className="g-label" role="columnheader" aria-label="Task">
          {corner}
        </div>
        <div ref={headTrack} className="g-track" role="columnheader">
          {grid}
          {months
            .filter((m) => todayPct === null || Math.abs(x(m) - todayPct) > 3) // not under the "today" label
            .map((m) => (
              <span key={m.toISOString()} className="g-tick" style={{ left: `${x(m)}%` }}>
                {ym(m)}
              </span>
            ))}
          {todayPct !== null && <span className="g-today" style={{ left: `${todayPct}%` }} />}
          {todayPct !== null && <span className="g-tick g-tick-today" style={{ left: `${todayPct}%` }}>today</span>}
        </div>
      </div>

      {rows.length === 0 && <p className="g-empty">No tasks match these filters.</p>}

      <div className="g-rows" role="rowgroup">
        {links.length > 0 && <DepLines links={links} spans={spans} w={tw} />}
        {rows.map((r, idx) => {
          if (r.kind === 'nogrant') {
            return (
              <div key={r.key} className="g-row g-group g-nogrant" role="row">
                <div className="g-label" role="cell">(No grant)</div>
                <div className="g-track" role="cell" />
              </div>
            );
          }
          const n = r.node!;
          const [left, right] = spans[idx] ?? [null, null];
          const grant = r.kind === 'grant';
          const type = typeLabel(n.type).toLowerCase();
          const g = !grant && r.group ? byId.get(r.group) : undefined;
          const gs = g ? day(g.start) : null;
          const ge = g ? day(g.end) : null;
          const people = grant ? [] : barPeople(b, n.id, filters, byName);
          const outs = grant ? [] : outputsOf(n.id);
          const outsDone = outs.filter((o) => o.done).length;
          const acts = b.openActions[n.id] ?? [];
          const { marks, beside } = actionMarks(acts);
          // The date bubbles in date order, moved clear of each other (old place()). Before the track is
          // measured: one day's bubbles side by side.
          const dated = [...marks].sort((p, q) => p.day.localeCompare(q.day));
          const xs = dated.map((m) => (pct(day(m.day)!) * tw) / 100);
          const placed = tw ? placeBubbles(xs) : xs.map((x, k) => x + dated[k]!.nth * BUBBLE_STEP);
          // Anyone who may comment adds one, on a task (a grant's panel has no add form either).
          const plus = !grant && b.canComment ? plusBubble(n, isStalled(n, acts)) : null;
          const comments = b.commentCounts[n.id] ?? 0;
          const cmts = plural(comments, 'comment');
          const dl = grant ? null : daysLeft(n.end, todayStr);
          const late = dl !== null && dl < 0 && n.status !== 'DONE';
          const lead = grant ? '' : (leads.get(n.id) ?? '');
          const drop = !grant && dragging && dragging !== n.id && canParent(b, dragging, n.id);
          // Extras after the bar end, at most to the row's end: the text gives way to the chips there.
          // Near the right edge they go before the bar, or inside its end.
          const flip = right !== null && right > 97;
          const flipAt = flip ? ((left ?? right!) > 25 ? left! : right!) : 0;
          // The chips start after a date bubble that would cover them (old: they took place() too). Their
          // width, estimated: 6px padding, about 5px a character, 19px a chip with its gap; the ✓ with a count is wider.
          const endText = grant ? n.end : dl !== null ? fmtLeft(dl) : '';
          const doneN = b.doneActions?.[n.id]?.count ?? 0;
          const chips = (lookOf(n) ? 1 : 0) + beside.length + (plus ? 1 : 0) + (doneN ? (doneN > 1 ? 1.3 : 1) : 0);
          const endPx = right !== null ? (right * tw) / 100 : 0;
          const shift =
            right !== null && !flip && tw ? Math.round(extrasAt(endPx, 6 + (endText ? endText.length * 5 + 4 : 0) + chips * 19, placed) - endPx) : 0;
          const focused = grant && focus === n.id;

          return (
            <div
              key={r.key}
              className={`g-row ${grant ? 'g-group' : `t-${type}`} ${!grant && idx % 2 === 1 ? 'odd' : ''} ${selected === n.id ? 'is-selected' : ''} ${over === n.id && drop ? 'is-drop' : ''}`}
              role="row"
              onDragOver={(e) => {
                if (!drop) return;
                e.preventDefault();
                setOver(n.id);
              }}
              onDragLeave={() => over === n.id && setOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                const child = e.dataTransfer.getData('text/plain');
                setOver(null);
                setDragging(null);
                if (child && canParent(b, child, n.id)) onReparent(child, n.id);
              }}
            >
              <div className="g-label" role="cell" style={grant ? undefined : { paddingLeft: `${18 + r.depth * 16}px` }}>
                {outs.length > 0 && (
                  <Link
                    to="/outputs"
                    search={{ node: n.id }}
                    className={`g-outs ${outsDone === outs.length ? 'done' : ''}`}
                    title={`${outs.map((o) => `${o.done ? '◆' : '◇'} ${o.title} (${o.stage})`).join('\n')}\nClick to open in Outputs`}
                    aria-label={`${plural(outs.length, 'output')}, open in Outputs`}
                  >
                    {outs.length}
                  </Link>
                )}
                <button
                  type="button"
                  className="g-label-btn"
                  onClick={() => onSelect(n.id)}
                  aria-label={`${n.id} · ${n.label}`}
                  draggable={b.canEdit && !grant}
                  onDragStart={(e) => {
                    card.current?.hide();
                    e.dataTransfer.setData('text/plain', n.id);
                    e.dataTransfer.effectAllowed = 'move';
                    setDragging(n.id);
                  }}
                  onDragEnd={() => {
                    setDragging(null);
                    setOver(null);
                  }}
                  {...hover(() => nodeCard(n))}
                >
                  <span className={`g-type t-${type}`}>{grant ? '◆' : typeLabel(n.type)}</span>
                  <span className="g-name">{n.label}</span>
                </button>
                {grant && b.canFocus && (
                  <button
                    type="button"
                    className={`g-focus ${focused ? 'on' : ''}`}
                    onClick={() => onFocus(focused ? undefined : n.id)}
                    title={focused ? 'Back to the full chart' : 'Show only this grant, with every task in it'}
                  >
                    {focused ? '✕ Exit focus' : 'Focus & expand'}
                  </button>
                )}
                {people.length > 0 && (
                  <span className="g-people">
                    {people.slice(0, MAX_PEOPLE).map((p) => (
                      <Ring key={`${p.person}/${p.role}`} {...p} />
                    ))}
                    {people.length > MAX_PEOPLE && (
                      <span className="g-more" title={people.slice(MAX_PEOPLE).map((p) => `${p.person} — ${p.role}`).join('\n')}>
                        +{people.length - MAX_PEOPLE}
                      </span>
                    )}
                  </span>
                )}
                {!grant && (comments > 0 || b.canComment) && (
                  <button
                    type="button"
                    className={`g-cmt ${comments ? 'on' : ''}`}
                    onClick={() => onThread(n.id)}
                    aria-label={comments ? cmts : 'Comments'}
                    {...hover(() => <CommentCard count={comments} last={b.lastComment?.[n.id]} />)}
                  >
                    {comments || '…'}
                  </button>
                )}
              </div>
              <div className="g-track" role="cell">
                {grid}
                {gs && ge && <span className="g-band" style={{ left: `${pct(gs)}%`, width: `${Math.max(pct(ge) - pct(gs), 0)}%` }} title={`${g!.label}: ${g!.start} → ${g!.end}`} />}
                {todayPct !== null && <span className="g-today" style={{ left: `${todayPct}%` }} />}
                {left !== null && right !== null && (
                  <button
                    type="button"
                    className={`g-bar s-${n.status.toLowerCase()} ${grant ? 'g-bar-grant' : ''} ${late ? 'late' : ''}`}
                    style={{ left: `${left}%`, width: `${Math.max(right - left, MIN_BAR)}%` }}
                    onClick={() => onSelect(n.id)}
                    aria-label={`${n.label}, ${n.start || 'no start'} to ${n.end || 'no end'}, ${n.status}`}
                    {...hover(() => nodeCard(n))}
                  >
                    {grant ? n.label : lead.split(' ')[0]}
                  </button>
                )}
                {left === null && (
                  // The bubbles with no place on the axis follow the note (old Actions.gs:575-577). An undated
                  // grant (a new one has no dates) gets no note, but keeps its folder and actions.
                  <span className="g-extras g-undated">
                    {!grant && (
                      <button type="button" className="g-nodates" onClick={() => onSelect(n.id)}>
                        no dates — click to set{lead ? ` · lead ${lead.split(' ')[0]}` : ''}
                      </button>
                    )}
                    {doneBubble(n)}
                    {folderBubble(n)}
                    {beside.map((a) => bubble(n, a, true))}
                    {plus}
                  </span>
                )}
                {outs
                  .filter((o) => day(o.targetDate))
                  .map((o) => (
                    <Link
                      key={o.id}
                      to="/outputs"
                      search={{ node: n.id }}
                      className={`g-out ${o.done ? 'done' : o.targetDate < todayStr ? 'late' : ''}`}
                      style={{ left: `${pct(day(o.targetDate)!)}%` }}
                      title={`${o.title} · ${o.kind} · ${o.stage}${o.targetDate ? ` · target ${o.targetDate}` : ''}\nClick to open in Outputs`}
                      aria-label={`Output ${o.title}, target ${o.targetDate}, open in Outputs`}
                    >
                      ◆
                    </Link>
                  ))}
                {/* On the deadline, else on the day it was added (old Actions.gs:594-604); clear of each other (place(), 579-588). */}
                {dated.map((m, k) => {
                  const off = Math.round((placed[k]! - xs[k]!) * 10) / 10;
                  return bubble(n, m.a, m.hollow, { left: off ? `calc(${pct(day(m.day)!)}% + ${off}px)` : `${pct(day(m.day)!)}%` });
                })}
                {right !== null && (
                  <span
                    className={`g-extras ${flip ? 'flip' : ''}`}
                    style={
                      flip
                        ? { right: `${100 - flipAt}%` }
                        : { left: shift ? `calc(${right}% + ${shift}px)` : `${right}%`, maxWidth: `calc(${100 - right}% + var(--g-tail)${shift ? ` - ${shift}px` : ''})` }
                    }
                  >
                    {/* Completed actions first, against the bar's end; then the days left and the other chips. */}
                    {doneBubble(n)}
                    {grant ? <span className="g-end">{n.end}</span> : dl !== null && <span className={`g-left ${late ? 'late' : ''}`}>{fmtLeft(dl)}</span>}
                    {folderBubble(n)}
                    {beside.map((a) => bubble(n, a, true))}
                    {plus}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {b.canEdit && <p className="g-hint">Tip: drag a task’s name onto another task to make it the parent.</p>}
      <HoverCard api={card} />
      {pop && (
        <ActionPopover
          b={b}
          pop={pop}
          onClose={closePop}
          onOpenTask={(id) => {
            setPop(null);
            onSelect(id);
          }}
        />
      )}
      {folder && <FolderModal nodeId={folder.id} label={folder.label} onClose={() => setFolder(null)} />}
    </div>
  );
}

/**
 * The width of the box `ref` is put on, in px: on each draw, and when it resizes without one (the window,
 * the details panel). 0 until it is measured.
 */
function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  // On each draw, before the paint: a width changed by a render (the panel opening) is never painted stale.
  // An observer's update comes only after a paint.
  useLayoutEffect(() => {
    const cw = ref.current?.clientWidth ?? 0;
    if (cw !== w) setW(cw);
  });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/**
 * Dependency lines over the rows (old marker #gdep and draw(), DP:7110, 7387-7392): from a parent's bar
 * end to its child's bar start, dashed when the parent sits under another grant. Under the bars and the
 * frozen name column; it takes no clicks. `w`: the track in px (the header's, the same column width).
 */
function DepLines({ links, spans, w }: { links: DepLink[]; spans: ([number, number] | null)[]; w: number }) {
  // Bars sit in % of the track; a path needs px.
  const px = (p: number) => (p * w) / 100;
  const mid = (i: number) => i * ROW_H + ROW_H / 2;
  return (
    <div className="g-deps" aria-hidden="true">
      <span />
      <div>
        {w > 0 && (
          <svg>
            <defs>
              <marker id="g-dep-arrow" viewBox="0 -5 10 10" refX={8} refY={0} markerUnits="userSpaceOnUse" markerWidth={8} markerHeight={8} orient="auto">
                <path d="M0,-4L8,0L0,4" />
              </marker>
            </defs>
            {links.map(({ from, to, cross }) => {
              const p = spans[from];
              const c = spans[to];
              if (!p || !c) return null;
              const [pl, pr] = p;
              const [cl] = c;
              return (
                <path
                  key={`${from}>${to}`}
                  className={cross ? 'cross' : undefined}
                  d={depPath(px(pl + Math.max(pr - pl, MIN_BAR)), mid(from), px(cl), mid(to))}
                  markerEnd="url(#g-dep-arrow)"
                />
              );
            })}
          </svg>
        )}
      </div>
    </div>
  );
}

/** The hover card's handle: the chart shows, moves and hides it without drawing itself again. */
interface CardApi {
  show: (e: PointerEvent, body: ReactNode) => void;
  move: (e: PointerEvent) => void;
  hide: () => void;
}

/** The dark card by the mouse (old #gantt-tip and moveTip, DP:6940, 7165). */
function HoverCard({ api }: { api: Ref<CardApi> }) {
  const [body, setBody] = useState<ReactNode>(null);
  const box = useRef<HTMLDivElement>(null);
  const at = useRef({ x: 0, y: 0 });
  // Below right of the pointer; left of it near the right edge, above it near the bottom.
  // The height is read after the left is set: the card fits its text, so moved left it can widen and get shorter.
  const place = () => {
    const el = box.current;
    if (!el) return;
    const { x, y } = at.current;
    const w = el.offsetWidth;
    el.style.left = `${x + 14 + w > window.innerWidth ? Math.max(4, x - 10 - w) : x + 14}px`;
    const h = el.offsetHeight;
    el.style.top = `${y + 14 + h > window.innerHeight ? Math.max(4, y - 10 - h) : y + 14}px`;
  };
  useImperativeHandle(
    api,
    () => ({
      show: (e, content) => {
        at.current = { x: e.clientX, y: e.clientY };
        setBody(content);
      },
      move: (e) => {
        at.current = { x: e.clientX, y: e.clientY };
        place();
      },
      hide: () => setBody(null),
    }),
    [],
  );
  useLayoutEffect(place);
  return body ? (
    <div ref={box} className="g-card" role="tooltip">
      {body}
    </div>
  ) : null;
}

/** On a name or a bar (old showTip, DP:7153-7163): the lead's photo and name, the dates, the description. */
export function NodeCard({ n, lead, image, personId, canEdit }: { n: GanttNode; lead: string; image: string; personId?: string; canEdit: boolean }) {
  const photo = usePhoto(image, lead, initials(lead), personId);
  const dates = n.start || n.end ? `${n.start || '?'} → ${n.end || '?'}` : '';
  return (
    <>
      {lead && (
        <div className="g-card-hd">
          <span className="g-card-av">{photo}</span>
          <span className="g-card-lead">
            Lead
            <br />
            <b>{lead}</b>
          </span>
        </div>
      )}
      <div className="g-card-title">{n.label}</div>
      <div className="g-card-meta">{[n.id, dates, n.status].filter(Boolean).join(' · ')}</div>
      {n.description ? (
        <div className="g-card-desc">{n.description}</div>
      ) : (
        isGrant(n) && <div className="g-card-desc none">No description yet{canEdit ? ' — click to add one' : ''}.</div>
      )}
    </>
  );
}

/** On the folder: what it holds (old Files tip, DP:4744-4747). */
export function FolderCard({ what, empty }: { what: string; empty: boolean }) {
  return (
    <>
      <div className="g-card-meta">
        <b>Files</b> · {what}
      </div>
      <div className="g-card-more">{empty ? 'click to add files or notes' : 'click to open'}</div>
    </>
  );
}

/** On the comment button (old showCommentTip, DP:7178-7181): the newest comment. */
export function CommentCard({ count, last }: { count: number; last?: GanttLastComment }) {
  if (!count) return <div className="g-card-desc">No comments yet — click to add.</div>;
  if (!last) return <div className="g-card-more">{plural(count, 'comment')} — click to open</div>;
  return (
    <>
      <div className="g-card-meta">
        {hubDateTime(last.at)} · <b>{last.author}</b>
        {last.status && <span className="g-card-badge">→ {last.status}</span>}
      </div>
      {last.text && <div className="g-card-desc">{last.text}</div>}
      <div className="g-card-more">{count > 1 ? `+${count - 1} more — click to open` : 'click to open'}</div>
    </>
  );
}
